import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useMap } from '../map/MapContext';
import {
  NEXRAD_COLOR_SCALE,
  WEATHER_LAYER_IDS,
  WEATHER_SOURCE_IDS,
  type HazardType,
  getHazardColor,
} from '../map/weather-sources';
import { WebWeatherService, type WeatherMapData } from '../services/WebWeatherService';

interface WxLayerToggle {
  id: string;
  label: string;
  visible: boolean;
}

const INITIAL_LAYERS: WxLayerToggle[] = [
  { id: WEATHER_LAYER_IDS.nexrad, label: 'NEXRAD Radar', visible: false },
  { id: WEATHER_LAYER_IDS.sigmets, label: 'SIGMETs', visible: false },
  { id: WEATHER_LAYER_IDS.airmets, label: 'G-AIRMETs', visible: false },
  { id: WEATHER_LAYER_IDS.pireps, label: 'PIREPs', visible: false },
];

const HAZARD_TYPES: { type: HazardType; label: string }[] = [
  { type: 'convective', label: 'Convective' },
  { type: 'icing', label: 'Icing' },
  { type: 'turbulence', label: 'Turbulence' },
  { type: 'ifr', label: 'IFR' },
  { type: 'mountain-obscuration', label: 'Mtn Obscuration' },
  { type: 'volcanic-ash', label: 'Volcanic Ash' },
];

export const WeatherPanel: React.FC = () => {
  const { map } = useMap();
  const [layers, setLayers] = useState(INITIAL_LAYERS);
  const [mapData, setMapData] = useState<WeatherMapData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const serviceRef = useRef(new WebWeatherService());

  const refreshMapWeather = useCallback(async () => {
    if (!map) return;
    const bounds = map.getBounds();
    setLoading(true);
    setError(null);
    try {
      const result = await serviceRef.current.fetchMapLayersForBbox(
        bounds.getSouth(), bounds.getNorth(), bounds.getWest(), bounds.getEast(),
      );
      const sources = [
        [WEATHER_SOURCE_IDS.sigmets, result.sigmets],
        [WEATHER_SOURCE_IDS.airmets, result.airmets],
        [WEATHER_SOURCE_IDS.pireps, result.pireps],
      ] as const;
      for (const [sourceId, data] of sources) {
        (map.getSource(sourceId) as GeoJSONSource | undefined)?.setData(data);
      }
      setMapData(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Current map weather is temporarily unavailable');
    } finally {
      setLoading(false);
    }
  }, [map]);

  useEffect(() => {
    void refreshMapWeather();
  }, [refreshMapWeather]);

  const toggleLayer = useCallback((id: string) => {
    setLayers((prev) => prev.map((l) => {
      if (l.id !== id) return l;
      const next = { ...l, visible: !l.visible };
      if (map) {
        try { map.setLayoutProperty(id, 'visibility', next.visible ? 'visible' : 'none'); } catch { /* */ }
      }
      return next;
    }));
  }, [map]);

  return (
    <div>
      {/* Layer toggles */}
      <div className="panel-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div className="panel-section-title" style={{ marginBottom: 0 }}>Weather Layers</div>
          <button className="btn btn-secondary" onClick={() => void refreshMapWeather()} disabled={!map || loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {error && <div className="panel-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
        {mapData && (
          <div style={{ color: 'var(--text-muted)', fontSize: 11, padding: '7px 0 3px' }}>
            Updated {new Date(mapData.fetchedAt).toLocaleTimeString()} · {mapData.sigmets.features.length} SIGMET · {mapData.airmets.features.length} G-AIRMET · {mapData.pireps.features.length} PIREP
          </div>
        )}
        {mapData?.warnings.map((warning) => (
          <div key={warning} className="status-yellow" style={{ fontSize: 11, padding: '2px 0' }}>{warning}</div>
        ))}
        {layers.map((l) => (
          <div key={l.id} className="list-item">
            <label className="toggle" style={{ flex: 1 }}>
              <span
                className={`toggle-box ${l.visible ? 'checked' : ''}`}
                onClick={() => toggleLayer(l.id)}
              />
              <span>{l.label}</span>
            </label>
          </div>
        ))}
      </div>

      {/* NEXRAD Legend */}
      <div className="panel-section">
        <div className="panel-section-title">NEXRAD Reflectivity (dBZ)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {NEXRAD_COLOR_SCALE.map((entry) => (
            <div
              key={entry.dbz}
              style={{
                width: 22, height: 18, background: entry.color, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, color: '#000', fontWeight: 600,
              }}
              title={`${entry.dbz} dBZ – ${entry.label}`}
            >
              {entry.dbz}
            </div>
          ))}
        </div>
      </div>

      {/* Hazard type legend */}
      <div className="panel-section">
        <div className="panel-section-title">Hazard Types</div>
        {HAZARD_TYPES.map(({ type, label }) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: getHazardColor(type), display: 'inline-block' }} />
            <span style={{ fontSize: 12 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* METAR / TAF placeholder */}
      <div className="panel-section">
        <div className="panel-section-title">METAR / TAF</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>
          Tap an airport on the map to fetch current METAR and TAF data.
        </div>
      </div>
    </div>
  );
};
